import {
  BooleanFilter,
  EnumFilter,
  SelectFilter,
  WbSearch,
  WbTable,
  WbResponsiveTableToolbar,
  WbTooltip,
  WbSafeNunjucksApi,
  TableCellProps,
} from '@agilelab/plugin-wb-platform';
import { Button, Checkbox, Radio, useTheme } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { CSSProperties, useMemo, useState } from 'react';
import {
  ActionsContextProvider,
  DataRootPathContextProvider,
  useAction,
  useDataPath,
} from '../CustomView';
import { CustomViewRegister } from '../CustomView/registry';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import _, { get, isUndefined } from 'lodash';
import { getChildren } from '../CustomView';
import { LinkIcon } from '../Icons';
import { WbStatusBadge } from '../WbStatusBadge';
import {
  CustomViewNunjucksRenderer,
  useCustomViewNunjucksRenderer,
} from '../CustomView/context';

type RowData = Record<string, any>;

import { z } from 'zod';

export const CustomViewTableFilterTypeSchema = z.enum([
  'search',
  'boolean',
  'multiselect',
  'select',
]);
export type CustomViewTableFilterType = z.infer<
  typeof CustomViewTableFilterTypeSchema
>;

export const CustomViewTableFilterSchema = z.object({
  column: z.string(),
  path: z.string().optional(),
  type: CustomViewTableFilterTypeSchema,
  placeholder: z.string().optional(),
});

export type CustomViewTableFilter = z.infer<typeof CustomViewTableFilterSchema>;

export const TableMaxHeightSchema = z
  .union([
    z.number(),
    z.string().regex(/^\d+(\.\d+)?(px|vh|vw|em|rem)$|^calc\(.+\)$/),
  ])
  .optional();

type ResolveFilterValueFn = (rowData: RowData) => any;

type ProcessedFilter = CustomViewTableFilter & {
  id: string;
  label: string;
  resolveFilterValue: ResolveFilterValueFn;
};

const getColumnId = (index: number) => {
  return `__column_${index}`;
};

export const resolveColumnValue = (
  columnProps: Record<string, any>,
  rowData: RowData,
  nunjucksRenderer: WbSafeNunjucksApi,
) => {
  // path "wins" over value as in propertiesProcessor.ts
  if (columnProps.path !== undefined)
    return columnProps.path === '' ? rowData : get(rowData, columnProps.path);
  if (columnProps.value)
    return nunjucksRenderer.render(columnProps.value, rowData);

  return undefined;
};

export const resolveFilterValueByPath = (path: string, rowData: RowData) => {
  return path === '' ? rowData : get(rowData, path);
};

export const isColumnValueEmpty = (value: any) => {
  if (!value) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  return false;
};

export const processShowWhenRow = (
  showRowWhen: { value?: string; equals?: string; notEquals?: string },
  row: RowData,
  nunjucksRenderer: CustomViewNunjucksRenderer,
): boolean => {
  if (showRowWhen?.value) {
    const parsedValue =
      nunjucksRenderer.render(showRowWhen.value, row) ?? showRowWhen.value;
    const parsedEqual = showRowWhen.equals;
    const parsedNotEqual = showRowWhen.notEquals;

    if (parsedEqual !== undefined && parsedValue !== parsedEqual?.toString())
      return false;
    if (
      parsedNotEqual !== undefined &&
      parsedValue === parsedNotEqual?.toString()
    )
      return false;
  }
  return true;
};
export const explodeTreeView = (
  list: Record<string, any>[],
  key: string,
  level: number = 0,
  path: number[] = [],
) =>
  list.flatMap((elem: any, i: number) => {
    let result = [
      Object.assign(
        {
          _treeview: {
            level,
            hasChildren: key in elem,
            id: [path.length, i].join('/'),
            path: path.concat([i]).map((p, k) => [k, p].join('/')),
          },
        },
        elem,
      ),
    ];
    if (key in elem)
      result = result.concat(
        explodeTreeView(elem[key], key, level + 1, path.concat([i])),
      );
    return result;
  });

/**
 * Pure filter-matching logic for a single filter type against a row value.
 */
export function matchesFilter(
  type: CustomViewTableFilterType,
  rowValue: any,
  currentValue: any,
): boolean {
  switch (type) {
    case 'multiselect':
      return !currentValue?.length || currentValue.includes(rowValue);
    case 'boolean':
      return !currentValue || rowValue === currentValue;
    case 'select':
    case 'search':
    default:
      if (!currentValue) return true;
      if (typeof rowValue !== 'string') return false;
      return rowValue.toLowerCase().includes(currentValue.toLowerCase());
  }
}

/**
 * Adds precomputed column sort values to a row, keyed by col.id.
 * The keys written here must match the `field` set by buildWbTableColumn —
 * together they form the sorting contract.
 */
export function precomputeRowSortValues(
  row: RowData,
  columns: Array<{ id: string; elem: { props: Record<string, any> } }>,
  nunjucksRenderer: WbSafeNunjucksApi,
): RowData {
  const resolvedColumnValues = columns.reduce<Record<string, any>>(
    (acc, curr) => ({
      ...acc,
      [curr.id]: resolveColumnValue(curr.elem.props, row, nunjucksRenderer),
    }),
    {},
  );
  return { ...row, ...resolvedColumnValues };
}

/**
 * Builds a WbTable column descriptor from a processed column.
 * `field` must equal `col.id`: WbTable reads `row[field]` for sorting,
 * and precomputeRowSortValues stores values at `row[col.id]`.
 */
export function buildWbTableColumn(
  col: {
    id: string;
    resizable: boolean;
    sortable: boolean;
    title: string;
    elem: any;
  } & Pick<TableCellProps<RowData>, 'width' | 'minWidth' | 'maxWidth'>,
): TableCellProps<RowData> {
  return {
    uniqueFieldId: col.id,
    field: col.id,
    width: col.width,
    minWidth: col.minWidth,
    maxWidth: col.maxWidth,
    resizable: col.resizable,
    sortable: col.sortable,
    headerName: col.title,
    fieldRender: () => col.elem,
  };
}

CustomViewRegister.register({
  id: 'table',
  nunjucksPropsPaths: ['showRowWhen.value'],
  function: function TableView(props: Record<string, any>) {
    const hideEmptyColumns = props.hideEmptyColumns || false;
    const clickFn = useAction(props.click);
    const [closedNodes, setClosedNodes] = useState<string[]>([]);
    const parent = useDataPath('');
    const nunjucksRenderer = useCustomViewNunjucksRenderer();
    let list: RowData[] = useDataPath(props.path) ?? [];

    list = list.map((row: RowData) => {
      return Object.assign({}, row, {
        _parent: parent,
      });
    });
    const showRowWhen = props.showRowWhen;

    const rows = list
      .filter(row => processShowWhenRow(showRowWhen, row, nunjucksRenderer))
      .filter(
        (row: any) =>
          !closedNodes.some((closedNode: string) => {
            return row._treeview.path.slice(0, -1).indexOf(closedNode) >= 0;
          }),
      );
    const onClick = (ev: any, rowData: any, idx: number) => {
      const tag = (ev.target as HTMLElement).tagName ?? '';
      const tagParent = (ev.target as HTMLElement).parentElement?.tagName ?? '';
      // bypass if user clicks on a link
      if (tag !== 'A' && tagParent !== 'A') clickFn(rowData, idx);
    };

    const columns = getChildren(props).map((elem, index) => {
      const defaultSortable = ['string'].includes(elem.props.type);

      return {
        elem,
        width: elem.props.width || undefined,
        minWidth: elem.props.minWidth || undefined,
        maxWidth: elem.props.maxWidth || undefined,
        resizable: elem.props.resizable ?? true,
        title: elem.props.label || elem.props.title,
        id: getColumnId(index),
        path: elem.props.path,
        align: elem.props.align || '',
        showWhenExistsPath: elem.props.showWhenExistsPath || '',
        showWhen: elem.props.showWhen,
        sortable:
          ['string'].includes(elem.props.type) &&
          !isUndefined(elem.props.sortable)
            ? elem.props.sortable
            : defaultSortable,
      };
    });

    const filteredColumns = columns.filter(column => {
      if (hideEmptyColumns) {
        if (
          !rows.some(
            row =>
              row &&
              !isColumnValueEmpty(
                resolveColumnValue(column.elem.props, row, nunjucksRenderer),
              ),
          )
        )
          return false;
      }
      if ((column.showWhenExistsPath || '') === '') return true;
      return !!_.get(
        (list as Record<string, any>)._parent,
        column.showWhenExistsPath,
      );
    });

    const rowStyle: CSSProperties = {};
    if (props.click) rowStyle.cursor = 'pointer';

    const filters: unknown[] = useMemo(
      () => props.filters || [],
      [props.filters],
    );

    const processedFilters = useMemo(() => {
      const actualFilters: ProcessedFilter[] = [];
      if (filters.length === 0) return [];

      const columnsPropsByName = filteredColumns.reduce((result, col) => {
        result.set(col.title, col.elem.props);
        return result;
      }, new Map<string, Record<string, any>>());

      filters.forEach((filter: unknown, i) => {
        try {
          const parsedFilter = CustomViewTableFilterSchema.parse(filter);

          const columnProps = columnsPropsByName.get(parsedFilter.column);

          if (!columnProps)
            throw new Error(`${parsedFilter.column} is not a valid column`);

          const getResolveFilterValueFn = (): ResolveFilterValueFn => {
            const filterPath = parsedFilter.path;

            if (filterPath !== undefined)
              return rowData => resolveFilterValueByPath(filterPath, rowData);

            return rowData =>
              resolveColumnValue(columnProps, rowData, nunjucksRenderer);
          };

          actualFilters.push({
            id: parsedFilter.column,
            label: parsedFilter.column,
            resolveFilterValue: getResolveFilterValueFn(),
            ...parsedFilter,
          });
        } catch (e) {
          if (e instanceof Error)
            // eslint-disable-next-line no-console
            console.error(
              `Invalid filter configuration at index ${i}. ${e.message}.`,
            );
        }
      });

      return actualFilters;
    }, [filters, filteredColumns, nunjucksRenderer]);

    const initFilters = () => {
      const initialFiltersState: Record<string, any> = {};

      if (processedFilters.length === 0) return initialFiltersState;

      const initialFilterValues: Record<string, any> = {
        multiselect: [],
        boolean: false,
      };

      processedFilters.forEach(filter => {
        initialFiltersState[filter.id] = initialFilterValues[filter.type] || '';
      });
      return initialFiltersState;
    };

    const [filtersState, setFiltersState] = useState(initFilters());

    const processedRows = useMemo(() => {
      const filterRow = (row: RowData) =>
        processedFilters.every(({ type, id, resolveFilterValue }) =>
          matchesFilter(type, resolveFilterValue(row), filtersState[id]),
        );

      return (
        rows
          .filter(row => filterRow(row))
          // precompute and add to the row object the resolved value for each column, used for sorting
          .map(row =>
            precomputeRowSortValues(row, filteredColumns, nunjucksRenderer),
          )
      );
    }, [
      rows,
      processedFilters,
      filtersState,
      nunjucksRenderer,
      filteredColumns,
    ]);

    const handleFilterChange = (filterId: string, value: any) => {
      setFiltersState(prev => ({
        ...prev,
        [filterId]: value,
      }));
    };

    const resetFilters = () => {
      setFiltersState(initFilters());
    };

    const renderFilters = () => {
      return processedFilters.map(
        ({ type, placeholder, resolveFilterValue, id, label }) => {
          const uniqueOptions = new Set();

          const currentValue = filtersState[id];

          rows.forEach(row => {
            const rowValue = resolveFilterValue(row);
            if (
              rowValue !== null &&
              rowValue !== undefined &&
              typeof rowValue !== 'object'
            ) {
              uniqueOptions.add(rowValue);
            }
          });

          const options: any[] = [...uniqueOptions];

          switch (type) {
            case 'search':
              return (
                <WbSearch
                  key={id}
                  value={currentValue}
                  style={{ maxWidth: '200px' }}
                  onChange={e => handleFilterChange(id, e)}
                  placeholder={placeholder || `Filter by ${label}`}
                />
              );
            case 'boolean':
              return (
                <BooleanFilter
                  key={id}
                  field={label}
                  value={currentValue}
                  onChange={e => handleFilterChange(id, e)}
                />
              );
            case 'multiselect':
              return (
                <EnumFilter<string>
                  key={id}
                  field={label}
                  renderOption={o => snakeCaseToTitleCase(o)}
                  renderValue={v => snakeCaseToTitleCase(v)}
                  value={currentValue}
                  onChange={e => handleFilterChange(id, e)}
                  onSearch={v =>
                    options.filter(o => new RegExp(v, 'ig').test(o))
                  }
                  options={options}
                />
              );
            case 'select':
            default:
              return (
                <SelectFilter<string>
                  key={id}
                  field={label}
                  renderOption={o => snakeCaseToTitleCase(o)}
                  renderValue={v => snakeCaseToTitleCase(v)}
                  value={currentValue}
                  onChange={e => handleFilterChange(id, e)}
                  onSearch={v =>
                    options.filter(o => new RegExp(v, 'ig').test(o))
                  }
                  options={options}
                />
              );
          }
        },
      );
    };

    const wbTableColumns: TableCellProps<RowData>[] =
      filteredColumns.map(buildWbTableColumn);

    const maxHeightResult = TableMaxHeightSchema.safeParse(props.maxHeight);
    if (!maxHeightResult.success && props.maxHeight !== undefined) {
      // eslint-disable-next-line no-console
      console.warn(
        `[table] Invalid maxHeight value: "${props.maxHeight}". Expected a number or a CSS length string (px, vh, vw, em, rem, calc(...)). The prop will be ignored.`,
      );
    }
    const maxHeight: number | string | undefined = maxHeightResult.data;

    return (
      <ActionsContextProvider actions={{ getMode: () => 'table' }}>
        {!!processedFilters.length && (
          <WbResponsiveTableToolbar onClear={resetFilters} marginBottom>
            <WbResponsiveTableToolbar.Filters>
              {renderFilters()}
            </WbResponsiveTableToolbar.Filters>
          </WbResponsiveTableToolbar>
        )}
        <WbTable
          stickyHeader={!!maxHeight}
          styles={
            maxHeight
              ? { container: { maxHeight, overflowY: 'auto' } }
              : undefined
          }
          components={{
            tableContent: {
              columns: wbTableColumns,
              rows: processedRows,
              rowsWrapper: DataRootPathContextProvider,
              onRowClick: (element, event, idx) => {
                onClick(event, element, idx);
              },
              treeProps: {
                expandedRowsIds: closedNodes,
                getRowChildren: r => r?._treeview ?? [],
                onExpandChange: newExpanded => setClosedNodes(newExpanded),
              },
            },
          }}
        />
      </ActionsContextProvider>
    );
  },
});

CustomViewRegister.register({
  id: 'radio',
  nunjucksPropsPaths: ['disabled'],
  function: function RadioButton(props: Record<string, any>) {
    const action = useAction(props.click);
    const data = useDataPath('');
    const nunjucksRenderer = useCustomViewNunjucksRenderer();

    const nunjucksDisabled = nunjucksRenderer.render(props.disabled, data);

    const disabled =
      nunjucksDisabled !== undefined ? !!JSON.parse(nunjucksDisabled) : false;

    return (
      <WbTooltip
        title={disabled ? props.disabledTooltip ?? '' : props.tooltip ?? ''}
      >
        <span>
          <Radio
            inputProps={{ 'aria-label': 'Select row' }}
            onClick={e => {
              e.stopPropagation();
              action(data);
            }}
            checked={Boolean(props.value)}
            color="primary"
            disabled={disabled}
          />
        </span>
      </WbTooltip>
    );
  },
});

CustomViewRegister.register({
  id: 'checkbox',
  nunjucksPropsPaths: ['disabled'],
  function: function RadioButton(props: Record<string, any>) {
    const action = useAction(props.click);
    const data = useDataPath('');
    const nunjucksRenderer = useCustomViewNunjucksRenderer();
    const nunjucksDisabled = nunjucksRenderer.render(props.disabled, data);

    const disabled =
      nunjucksDisabled !== undefined ? !!JSON.parse(nunjucksDisabled) : false;

    return (
      <WbTooltip
        title={disabled ? props.disabledTooltip ?? '' : props.tooltip ?? ''}
      >
        <span>
          <Checkbox
            inputProps={{ 'aria-label': 'Select row' }}
            onClick={e => {
              e.stopPropagation();
              action(data);
            }}
            checked={Boolean(props.value)}
            color="primary"
            disabled={disabled}
          />
        </span>
      </WbTooltip>
    );
  },
});

CustomViewRegister.register({
  id: 'enabled',
  function: function RadioButton(props: Record<string, any>) {
    const theme = useTheme();
    const color = props.value
      ? theme.palette.success.main
      : theme.palette.grey[300];
    return <CheckCircleIcon htmlColor={color} />;
  },
});

CustomViewRegister.register({
  id: 'status',
  function: function Status(props: Record<string, any>) {
    // if the value is not defined we should show nothing instead of KO
    if (props.value === undefined) return <></>;
    return <WbStatusBadge ok={props.value} />;
  },
});

CustomViewRegister.register({
  id: 'row_link',
  function: function RowLink(props: Record<string, any>) {
    const click = useAction(props.click);
    const rowData = useDataPath('');
    if (!props.value) return <></>;
    return (
      <Button
        onClick={() => click(rowData)}
        aria-label={props.label}
        title={props.label}
        size="small"
      >
        <LinkIcon fontSize="small" />
      </Button>
    );
  },
});
